import { I18n } from "@assets/strings";
import React, { JSX } from "react";
import styled from "styled-components";
import { alpha } from "@ui/styles/utils";
import { PrimaryBold } from "@ui/components/containers/primary";
import { row, Row, Stack } from "./layout";

interface TabViewsProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> {
	items: readonly T[];
	labelKey: string;
	tabIndex: number;
	onClick: (value: number, tab: T) => void;
}

export function TabView<T>({ items, labelKey, tabIndex, onClick, ...htmlProps }: TabViewsProps<T>): JSX.Element {
	return (
		<Container {...htmlProps}>
			{items.map((tab, index) => (
				<Tab selected={tabIndex === index} key={tab as string} onClick={() => onClick(index, tab)}>
					<Label selected={tabIndex === index}>{I18n.t(`${labelKey}.${tab}`)}</Label>
					<Bloc>
						{tabIndex === index && <BoldLine />}
						<Line />
					</Bloc>
				</Tab>
			))}
		</Container>
	);
}

interface VerticalTabViewProps extends Omit<TabViewsProps<string>, "onClick" | "items"> {
	items: readonly { label: string; Icon?: React.ReactElement }[];
	onClick: (index: number) => void;
	renderIcon?: () => JSX.Element;
}

export const VerticalTabView: React.FC<VerticalTabViewProps> = ({
	items,
	labelKey,
	tabIndex,
	onClick,
	renderIcon,
	...htmlProps
}) => {
	return (
		<Stack {...htmlProps} gap={30} align="baseline">
			{items.map(({ label, Icon }, index) => (
				<Row
					key={label}
					onClick={() => onClick(index)}
					gap={20}
					align="center"
					justify="center"
					style={{ cursor: "pointer" }}
				>
					{Icon ? <IconWrapper>{Icon}</IconWrapper> : null}
					<Label selected={tabIndex === index}>{I18n.t(`${labelKey}.${label}`)}</Label>
				</Row>
			))}
		</Stack>
	);
};

const Container = styled.div`
	display: flex;
	align-items: center;
`;

const Bloc = styled.div`
	position: relative;
	width: 100%;
`;

const BoldLine = styled.div`
	top: 0;
	position: absolute;
	height: 2.5px;
	width: 100%;
	background-color: ${({ theme }) => theme.colors.secondary};
`;

const Line = styled.div`
	top: 1px;
	position: absolute;
	width: 100%;
	height: 0.5px;
	background-color: ${({ theme }) => alpha(theme.colors.secondary, 50)};
`;

const Tab = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
	display: flex;
	align-items: center;
	flex-direction: column;
	cursor: pointer;
	width: 100%;
	gap: 30px;
`;

const Label = styled(PrimaryBold).withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
	color: ${({ theme, selected }) => (selected ? theme.colors.secondary : theme.colors.textPrimary)};
`;

const IconWrapper = styled.div`
	flex-shrink: 0;
	border-radius: 9px;
	width: 34px;
	height: 34px;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	${row(0, "center", "center")};
	> svg {
		width: 20px;
		height: 20px;
	}
`;
